import { Box, Stack } from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { useParams } from 'react-router'
import { Mutation_Root, Project_Goal_Annual_Report_Insert_Input, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { resolveAnnualReportMilestoneRoot } from 'src/routing/routing-utils'
import {
  deleteGoalAnnualReportMutation,
  queryGoalAnnualReportByGoalAndMilestoneId,
  queryMilestoneById,
  upsertGoalAnnualReportMutation,
} from 'src/screens/shared/implementation/details/milestoneQueries'
import { usePermissionsForMilestones } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { GOAL_CHAR, TEXT_LENGTH } from 'src/shared/constants/constants'
import {
  GOAL_MEASURE_RATING,
  MILESTONE_RESPONSIBLE_TYPE_TYPE,
  MILESTONE_STATUS_TYPE,
} from 'src/shared/constants/milestone-constants'
import { Option } from 'src/shared/form/control'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { maxChar, required } from 'src/shared/form/validation/validators'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Utils } from 'src/shared/utils/Utils'
import { useClient, useQuery } from 'urql'

interface Props {
  baseUrl: '/pf-kap' | '/pf-pgv'
}

interface GoalRatingForm {
  rating: string | null
  rating_comment: string | null
}

const decorators = createDecorators()

export const MilestoneGoalEditPage = ({ baseUrl }: Props): ReactElement => {
  const { projectId, milestoneId, milestonePath, goalId } = useParams()
  const project_id = parseInt(projectId as string)
  const goal_id = parseInt(goalId as string)
  const milestone_id = parseInt(milestoneId as string)
  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const process = Utils.resolveProcess(baseUrl)
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [goalTitle, setGoalTitle] = useState('')

  const [{ data, error }] = useQuery<{ milestone_by_pk: Query_Root['milestone_by_pk'] }, { id: number }>({
    query: queryMilestoneById,
    variables: { id: milestone_id },
  })

  if (error) {
    notificationService.operationFailed()
  }

  const milestone = data?.milestone_by_pk

  const { loading, canModifyGoals } = usePermissionsForMilestones(
    process,
    project_id,
    milestone?.status as MILESTONE_STATUS_TYPE,
    milestone?.responsible_type as MILESTONE_RESPONSIBLE_TYPE_TYPE,
  )

  const [initialValues, setInitialValues] = useState<GoalRatingForm>()
  const [ratingOptions, setRatingOptions] = useState<Option[]>([])

  useEffect(() => {
    const initData = async () => {
      const ratingOptions = Object.keys(GOAL_MEASURE_RATING).map((key) => ({
        label: `${getMessage(`label.goal.rating.${key}`)}`,
        value: key,
      }))
      setRatingOptions(ratingOptions)

      const { data } = await urqlClient
        .query<
          {
            project_goal_annual_report: Query_Root['project_goal_annual_report']
            project_goal_by_pk: Query_Root['project_goal_by_pk']
          },
          { goalId: number; milestoneId: number }
        >(queryGoalAnnualReportByGoalAndMilestoneId, { goalId: goal_id, milestoneId: milestone_id })
        .toPromise()

      if (data && data.project_goal_annual_report && data.project_goal_by_pk) {
        const goalAnnualReport = data?.project_goal_annual_report?.[0]

        setInitialValues({
          rating: goalAnnualReport?.rating ?? null,
          rating_comment: goalAnnualReport?.rating_comment ?? null,
        })

        const goalIdCharacter = getMessage(`label.goal.id.character`)
        setGoalTitle(`${data.project_goal_by_pk.name} (${goalIdCharacter}${data.project_goal_by_pk.sort_number})`)
      } else {
        notificationService.operationFailed()
      }
    }
    initData()
  }, [urqlClient, goal_id, milestone_id, notificationService, getMessage])

  let submit: any = () => {}
  let formValid = false

  const onSave = (event: any) => {
    submit(event)
  }

  const onSaveAndBack = async (event: any) => {
    try {
      await submit(event)
      formValid && onBack()
    } catch {
      // do nothing
    }
  }

  const onBack = () => {
    const goalsAndMeasuresRoute = resolveAnnualReportMilestoneRoot(baseUrl).nested.GoalsAndMeasures.params({
      projectId,
      milestoneId,
      milestonePath,
    })

    navigate(`${goalsAndMeasuresRoute}#${GOAL_CHAR}${goalId}`, { state: { expandedEntityId: `${GOAL_CHAR}${goalId}` } })
  }

  const onSubmitLocal = async (values: GoalRatingForm) => {
    const emptyRating = !values.rating && !values.rating_comment

    if (!emptyRating) {
      const goalAnnualReportModel = {
        milestone_id: milestone_id,
        project_goal_id: goal_id,
        rating: values.rating,
        rating_comment: values.rating_comment,
      }

      const { data } = await urqlClient
        .mutation<
          { insert_project_goal_annual_report_one: Mutation_Root['insert_project_goal_annual_report_one'] },
          { object: Project_Goal_Annual_Report_Insert_Input }
        >(upsertGoalAnnualReportMutation, { object: goalAnnualReportModel })
        .toPromise()

      if (data && data?.insert_project_goal_annual_report_one?.id) {
        notificationService.changesSaved()
        setInitialValues(values)
      } else {
        notificationService.operationFailed()
      }
    } else {
      const { data } = await urqlClient
        .mutation<{ delete_project_goal_annual_report: Mutation_Root['delete_project_goal_annual_report'] }>(
          deleteGoalAnnualReportMutation,
          {
            milestoneId: milestone_id,
            goalId: goal_id,
          },
        )
        .toPromise()

      if (data) {
        notificationService.changesSaved()
        setInitialValues(values)
      } else {
        notificationService.operationFailed()
      }
    }
  }

  if (!loading && !canModifyGoals) {
    return <NotAuthorized inEditPage />
  }

  return (
    <>
      <ScreenLayout
        title={getMessage('label.edit.goal.milestone.title', [goalTitle])}
        onBack={onBack}
        hasSecondLevelNavigation={false}
        actions={
          <>
            <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
            <SaveButton origin="header" onClick={onSave} />
          </>
        }
      >
        <PageLayout>
          <>
            {!loading && initialValues && (
              <>
                <HelpAndInstructions
                  labelKey={`label.help.milestone.goal.rating.${process.toLowerCase()}`}
                  defaultExpansion
                />
                <Box>
                  <Form<GoalRatingForm>
                    initialValues={initialValues}
                    onSubmit={onSubmitLocal}
                    decorators={decorators}
                    render={({ handleSubmit, valid }) => {
                      submit = handleSubmit
                      formValid = valid
                      return (
                        <form onSubmit={handleSubmit} noValidate>
                          <Stack spacing={2}>
                            <AutoCompleteField
                              required
                              name="rating"
                              label={getMessage('label.goal.rating')}
                              options={ratingOptions}
                              validate={required()}
                            />
                            <HtmlEditorField
                              required
                              name="rating_comment"
                              label={getMessage('label.goal.rating.comment')}
                              validate={maxChar(TEXT_LENGTH.L)}
                            />
                            <DirtyFormSpy />
                          </Stack>
                        </form>
                      )
                    }}
                  />
                </Box>
              </>
            )}
          </>
        </PageLayout>
      </ScreenLayout>
    </>
  )
}
